/**
 * DashboardOpenPositions — Open Positions card mounted at the TOP of the
 * Trader Dashboard (Feb 2026 UAT item 8).
 *
 * Sept 2026: replaced with a full port of the "Place a Trade" Open
 * Positions widget (routes/pages/PlaceATrade.jsx) — the more comprehensive
 * of the two. Same PositionsTable component, but now with full feature
 * parity:
 *   • Advanced Exit (OCO) — open/cancel one-cancels-other stop+target pairs
 *     straight from the dashboard, not just the terminal.
 *   • Live prices sourced from the Mansa equities cache (`/macro/all-equities`)
 *     instead of the market WebSocket — the WS hook here was wired with the
 *     wrong call signature (`useMarketSocket(setTickers)` instead of
 *     `useMarketSocket({ onTicks: setTickers })`), so PnL never actually
 *     refreshed live; this fixes that by matching PlaceATrade's proven path.
 *
 * Always renders (even with 0 positions) — PositionsTable has its own
 * "No open positions." empty row, matching PlaceATrade's behaviour.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, fmtKES, formatApiErrorDetail } from "@/lib/api";
import { PositionsTable } from "@/components/terminal/PositionsTable";
import { AdvancedExitPanel } from "@/components/terminal/AdvancedExitPanel";

export default function DashboardOpenPositions() {
    const [positions, setPositions] = useState([]);
    const [busy, setBusy] = useState(false);
    const [allEquitiesFlat, setAllEquitiesFlat] = useState([]);

    // Advanced Exit (OCO)
    const [ocoRows, setOcoRows] = useState([]);
    const [ocoAccess, setOcoAccess] = useState(false);
    const [ocoPanelPosition, setOcoPanelPosition] = useState(null);
    const [ocoDraft, setOcoDraft] = useState(null);

    const loadPositions = useCallback(async () => {
        try {
            const { data } = await api.get("/positions?status=open");
            setPositions(data || []);
        } catch {
            // Silent — the terminal has its own loud error surface; here on
            // the dashboard we just skip rendering rather than nag the user.
        }
    }, []);

    const loadOco = useCallback(async () => {
        try {
            const [{ data: list }, { data: access }] = await Promise.all([
                api.get("/oco/list?status=active"), api.get("/oco/access"),
            ]);
            setOcoRows(list.orders || []);
            setOcoAccess(Boolean(access.can_create));
        } catch {
            // Non-fatal — Advanced Exit simply won't be available this load.
        }
    }, []);

    useEffect(() => {
        loadPositions();
        loadOco();
        api.get("/macro/all-equities")
            .then(({ data }) => setAllEquitiesFlat(Array.isArray(data) ? data : []))
            .catch(() => { /* live prices just won't be available */ });
    }, [loadPositions, loadOco]);

    const ocoByPosition = useMemo(
        () => Object.fromEntries(ocoRows.map((r) => [r.position_id, r])),
        [ocoRows],
    );
    const livePrices = useMemo(
        () => Object.fromEntries(allEquitiesFlat.map((t) => [t.symbol, t.price])),
        [allEquitiesFlat],
    );

    const onClose = async (p) => {
        setBusy(true);
        try {
            const { data } = await api.post("/orders/close", { position_id: p.id });
            toast.success(`Closed ${p.symbol} · PnL ${fmtKES(data.pnl)}`);
            await loadPositions();
            await loadOco();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not close the position.");
        } finally {
            setBusy(false);
        }
    };

    const openAdvancedExit = (position) => {
        setOcoPanelPosition(position);
        setOcoDraft(null);
    };

    const cancelOco = async (row) => {
        try {
            await api.delete(`/oco/${row.id}`);
            toast.success("Advanced Exit strategy cancelled.");
            await loadOco();
        } catch {
            toast.error("Couldn't cancel that OCO order.");
        }
    };

    return (
        <div data-testid="dashboard-open-positions">
            <PositionsTable
                positions={positions}
                onClose={onClose}
                busy={busy}
                livePrices={livePrices}
                ocoByPosition={ocoByPosition}
                onOpenAdvancedExit={openAdvancedExit}
                onCancelOco={cancelOco}
            />
            <AdvancedExitPanel
                open={Boolean(ocoPanelPosition)}
                position={ocoPanelPosition}
                currentPrice={ocoPanelPosition ? allEquitiesFlat.find((t) => t.symbol === ocoPanelPosition.symbol)?.price : null}
                draft={ocoDraft}
                onDraftChange={setOcoDraft}
                onClose={() => { setOcoPanelPosition(null); setOcoDraft(null); }}
                onCreated={loadOco}
                canCreate={ocoAccess}
            />
        </div>
    );
}
